import { FC, createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { useField } from 'formik';
import { ServiceType } from 'types/graphql';

import { OptionTypes } from 'src/components/FormikComponents/DropDown';
import { OfferProducts, useServiceTypeOffers } from 'src/hooks/useServiceTypeOffers';
import { ProductOrder, ProductOrderItem } from 'src/types/AddMerchant/serviceRequirements';

import { useAddMerchantContext } from '../AddMerchantProvider';

interface ProductOrderContext {
    order: ProductOrder;
    addOrderItem: (orderItem: ProductOrder) => void;
    removeOrderItem: (productId: number) => void;
    setOrderItemQuantity: (productId: number, quantity: number) => void;
    setOrderItemMonthlyPrice: (productId: number, price: number) => void;
    setOrderItemOneOffPrice: (productId: number, price: number) => void;
    primaryProducts: ProductOrderItem[];
    secondaryProducts: ProductOrderItem[];
    totalMonthly: number;
    totalOneOff: number;
    total: number;
    onClearOrder: () => void;
    pricingOptions: OptionTypes[];
    serviceType: ServiceType;
    offerProducts: OfferProducts[];
    pricingOffers: object[];
}

const ProductOrderContext = createContext<Partial<ProductOrderContext>>({});

export const useProductOrderContext = () => useContext(ProductOrderContext);

const calculateTotals = ({ quantity, oneOffPrice, monthlyPrice }: ProductOrderItem) => {
    let totalOneOff = 0;
    let totalMonthly = 0;

    totalOneOff = oneOffPrice * quantity;
    totalMonthly = monthlyPrice * quantity;

    return { totalOneOff, totalMonthly };
};

export const ProductOrderProvider: FC = ({ children }) => {
    const { merchantApplication, serviceApplication } = useAddMerchantContext();
    const name = 'productOrder';
    const [{ value: order }, _, { setValue: setProductOrder }] = useField<ProductOrder>(name);
    const [{ value: contractTerm }] = useField({
        name: 'service.contractTerm',
    });

    const { pricingOptions, serviceType, offerProducts, pricingOffers } = useServiceTypeOffers();

    const [orderItems, setOrderItems] = useState<ProductOrderItem[]>([
        ...(serviceApplication?.serviceRequirements?.productOrder?.primaryProducts ??
            merchantApplication?.serviceRequirements?.productOrder?.primaryProducts ??
            []),
        ...(serviceApplication?.serviceRequirements?.productOrder?.secondaryProducts ??
            merchantApplication?.serviceRequirements?.productOrder?.secondaryProducts ??
            []),
        {},
    ]);

    const term = useMemo(() => {
        return contractTerm.replace(/\D/g, '');
    }, [contractTerm]);

    const overallData = useMemo(() => {
        const primaryProducts = orderItems.filter((el) => el.type === 'PRIMARY');
        const secondaryProducts = orderItems.filter((el) => el.type === 'SECONDARY');

        let totalPrimaryOneOff = 0;
        let totalPrimaryMonthly = 0;
        let totalSecondaryOneOff = 0;
        let totalSecondaryMonthly = 0;

        if (primaryProducts.length) {
            primaryProducts.forEach((el) => {
                const calculatedTotals = calculateTotals(el);

                totalPrimaryOneOff += calculatedTotals.totalOneOff || 0;
                totalPrimaryMonthly += calculatedTotals.totalMonthly || 0;
            });
        }

        if (secondaryProducts.length) {
            secondaryProducts.forEach((el) => {
                const calculatedTotals = calculateTotals(el);

                totalSecondaryOneOff += calculatedTotals.totalOneOff || 0;
                totalSecondaryMonthly += calculatedTotals.totalMonthly || 0;
            });
        }

        let totalSecondary = 0;

        if (totalSecondaryOneOff !== 0 && totalSecondaryMonthly !== 0) {
            totalSecondary = totalSecondaryOneOff + totalSecondaryMonthly * term;
        } else if (totalSecondaryOneOff !== 0) {
            totalSecondary = totalSecondaryOneOff;
        } else if (totalSecondaryMonthly !== 0) {
            totalSecondary = totalSecondaryMonthly * term;
        }

        let totalPrimary = 0;

        if (totalPrimaryOneOff !== 0 && totalPrimaryMonthly !== 0) {
            totalPrimary = totalPrimaryOneOff + totalPrimaryMonthly * term;
        } else if (totalPrimaryOneOff !== 0) {
            totalPrimary = totalPrimaryOneOff;
        } else if (totalPrimaryMonthly !== 0) {
            totalPrimary = totalPrimaryMonthly * term;
        }

        return {
            primaryProducts,
            secondaryProducts,
            totalMonthly: totalSecondaryMonthly + totalPrimaryMonthly,
            totalSecondaryMonthly,
            totalPrimaryMonthly,
            totalOneOff: totalSecondaryOneOff + totalPrimaryOneOff,
            total: totalSecondary + totalPrimary,
        };
    }, [orderItems, term]);

    useEffect(() => {
        setProductOrder({
            total: overallData.total,
            primaryProducts: overallData.primaryProducts,
            secondaryProducts: overallData.secondaryProducts,
            term: Number(term),
            totalSecondaryMonthly: overallData.totalSecondaryMonthly,
            totalPrimaryMonthly: overallData.totalPrimaryMonthly,
            totalSetup: overallData.totalOneOff,
        });
    }, [overallData]);

    const addOrderItem = (orderItem: ProductOrderItem) => {
        const orderItemExists = orderItems.find((el) => el.id === orderItem.id);

        if (!orderItemExists) {
            setOrderItems((previousState) => [...previousState, orderItem]);
        }
    };

    const removeOrderItem = (productId: number) => {
        setOrderItems((previousState) => previousState.filter((el) => el.id !== productId));
    };

    const setOrderItemQuantity = (productId: number, quantity: number) => {
        setOrderItems((previousState) =>
            previousState.map((el) => {
                if (el.id === productId) {
                    return { ...el, quantity };
                }

                return el;
            })
        );
    };

    const setOrderItemOneOffPrice = (productId: number, price: number) => {
        setOrderItems((previousState) =>
            previousState.map((el) => {
                if (el.id === productId) {
                    return { ...el, oneOffPrice: Number(price) };
                }

                return el;
            })
        );
    };

    const setOrderItemMonthlyPrice = (productId: number, price: number) => {
        setOrderItems((previousState) =>
            previousState.map((el) => {
                if (el.id === productId) {
                    return { ...el, monthlyPrice: Number(price) };
                }

                return el;
            })
        );
    };

    const onClearOrder = useCallback(() => {
        setOrderItems([]);
    }, [setOrderItems]);

    return (
        <ProductOrderContext.Provider
            value={{
                order,
                addOrderItem,
                removeOrderItem,
                setOrderItemQuantity,
                setOrderItemOneOffPrice,
                setOrderItemMonthlyPrice,
                onClearOrder,
                pricingOptions,
                serviceType,
                offerProducts,
                pricingOffers,
                ...overallData,
            }}
        >
            {children}
        </ProductOrderContext.Provider>
    );
};
